import { METRIC_AGGREGATE, UNIT_PREFIX } from "@const/pageResponse.constants";
import type { DashboardGraph } from "@dto/dashboard.dto";
import { BoxType, GraphBase, GraphTablePropertyItem, Metric } from "@dto/pageResponse.dto";
import { Unit } from "@dto/subsystemMetricsResponse.dto";
import { TimeseriesDTO, TimeseriesItem, TimeseriesMetadata } from "@dto/timeseriesResponse.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { UnitUtil } from "@dto/util/UnitUtil";
import { AbstractGraphPart, ColorMap } from "@parts/graph/AbstractGraphPart";
import getPathValue from '@rc-component/util/es/utils/get';
import { SortOrder } from "antd/es/table/interface";
import type { Layout, ScatterData } from "plotly.js";
import { ColorUtil } from "src/util/ColorUtil";
import { MenuNode } from "src/model/MenuNode";
import { GLOB } from "src/util/Glob";
import { nameof } from "ts-simple-nameof";
import { ColProps } from "../table/tableTypes";
import { CommonGraphProps, GraphData, GraphState, GraphType, KTM, LayoutProps, TICK_FORMAT_FIXED, TICK_FORMAT_SI, UidName, X_HOVER_TIME_FORMAT, YExtremes } from "./GraphTypes";

/**
 * Graph start/end time by type (short/day,long/[week,month,year]) in seconds
 */
export interface GraphRangeState {
  endShort: number;
  startDay: number;

  endLong: number;
  startWeek: number;
  startMonth: number;
  startYear: number;
}

export interface PlotDataMeta extends UidName {
  itemName: string;
  metric: string;
  /** Metric name that stands on its own, without the graph title for context */
  longLabel: string;
  /** Trace stands for a metric (legend lists metrics), not for an item */
  metricTrace: boolean;
  parent: string;
}

const HOVER_X_FORMAT = `%{x|${X_HOVER_TIME_FORMAT}}`;
const HOVER_X_STYLED = `<span style='font-size: 9px'>${HOVER_X_FORMAT}</span>`;

function sumax(tblData: KTM[], metric: string, stacked: boolean) {
  return tblData.map(ktm => ktm.data[metric].max || 0).reduce((prev, current) => stacked ? (prev + current) : Math.max(prev, current), 0);
}

/**
 * Helpers for plotting
 */
export class GraphPlotUtil {

  /**
   * Metric name key for when there's just one
   */
  static readonly SINGLE_ITEM = 'singleItem';
  /**
   * Maximum number of metrics to show them side by side in columns, otherwise in rows
   */
  static readonly MAX_GROUP_METRICS = 2;
  /**
   * Limit value to display formatted value with SI
   */
  static readonly MIN_SI_VALUE = 9;

  private readonly box: GraphBase & { type?: BoxType | DashboardGraph['type']; };
  private readonly common: CommonGraphProps;
  private readonly graphState: GraphState;

  private totalSumOffset: number = 0;

  private _itemsCountCurrentOthers: number = 0;
  private _itemsCountCurrentShown: number = 0;
  /**
   * Current view metric metadata shown/others count
   */
  get itemsCount() {
    return {
      others: this._itemsCountCurrentOthers,
      shown: this._itemsCountCurrentShown
    };
  }

  private _maxValue = 0;
  /** Set per updatePlotData call, see its hiddenItems parameter. */
  private hiddenItems?: ReadonlySet<string>;
  /**
   * Current view metric metadata max
   */
  get maxValue() { return this._maxValue; }

  readonly userSort: { col?: ColProps<KTM>['dataIndex']; order?: SortOrder; } = {};

  constructor(box: GraphBase, common: CommonGraphProps, data: GraphData, graphState: GraphState) {
    this.box = box;
    this.common = common || {};
    if (data) {
      this._maxValue = data.max;
    }
    this.graphState = graphState;
    if (this.isMetricItems() && this.box.sort?.order) {
      this.userSort.order = 'descend';
      this.userSort.col = [nameof<KTM>(k => k.data), GraphPlotUtil.SINGLE_ITEM, this.box.sort.order];
    }
    this.compareKTM = this.compareKTM.bind(this);
    this.isSummable = this.isSummable.bind(this);
  }

  private getKTMCmpVal(ktm: KTM) {
    if (this.isMetricItems()) {
      if (this.userSort.col && this.userSort.order) {
        return getPathValue(ktm, this.userSort.col as []);
      }
      return this.getMetric(ktm)?.order;
    }

    let metricMetas = Object.values(ktm.data);
    if (this.graphState.sortMetric) {
      metricMetas = [ktm.data[this.graphState.sortMetric]];
    }
    const vals = metricMetas.map((tm) => tm[this.graphState.orderBy]);
    if (vals.every(v => v == null)) return null;
    return vals.reduce((prev, act) => prev + act, 0);
  }

  protected compareKTM(a: KTM, b: KTM) {
    if (a.isLast() || b.isFirst()) return 1;
    if (b.isLast() || a.isFirst()) return -1;

    const metricCmp = this.cmpKtmIfMetric(a, b);
    if (metricCmp != null) return metricCmp;

    const aVal = this.getKTMCmpVal(a);
    const bVal = this.getKTMCmpVal(b);
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    return GLOB.naturalSort(bVal, aVal);
  }

  private getMetric(ktm: KTM) {
    return this.box.metrics.find(m => m.metric === ktm.name);
  }

  cmpKtmIfMetric(a: KTM, b: KTM) {
    const am = this.getMetric(a);
    const bm = this.getMetric(b);
    if (am?.legendTop && this.isMetricItems(am)) {
      if (bm?.legendTop && this.isMetricItems(bm))
        return bm.order - am.order;
      else
        return -1;
    } else if (bm?.legendTop && this.isMetricItems(bm))
      return 1;
  }

  /** Hidden by a legend square click, unlike `visible === false` set by the table filter. */
  static isUserHidden(pd: Partial<ScatterData>) {
    return pd.visible === 'legendonly';
  }

  /**
   * Hides the passed traces as a square click does and shows the rest; table-filtered traces
   * and traces of legendOnly metrics stay.
   * @param pds plot data, mutated
   * @param hiddenItems uids of the hidden traces
   * @returns true when any trace changed
   */
  applyHiddenItems(pds: Partial<ScatterData>[], hiddenItems: readonly string[]) {
    const hidden = new Set(hiddenItems);
    const legendOnly = this.legendOnlyLabels();
    let changed = false;
    for (const pd of pds) {
      const meta = pd.meta as PlotDataMeta;
      if (!pd.visible || legendOnly.has(meta?.metric)) continue;
      const visible = hidden.has(meta?.uid) ? 'legendonly' : true;
      if (pd.visible === visible) continue;
      pd.visible = visible;
      changed = true;
    }
    return changed;
  }

  /** Hidden by configuration, so no square click or hiddenItems sync may show them. */
  private legendOnlyLabels() {
    return new Set(this.box.metrics.filter(m => m.legendOnly).map(m => m.label));
  }

  /**
   * Returns predicate function to find passed uid/ metricName in plotData
   * @param uid
   * @param metricName optional metric name,
   * @returns function to check plot data
   */
  static traceMatcher(uid: string, metricName?: string) {
    return (pd: Partial<ScatterData>) => pd.meta?.uid === uid && (!metricName || metricName === GraphPlotUtil.SINGLE_ITEM || pd.legendgrouptitle.text === metricName);
  }

  /**
   * Sort passed plot data according to current passed table data.
   * Must be followed by {@link GraphPlotUtil.liftLinesAboveAreas}.
   * @param pds plot data array
   * @param table table data array
   */
  sortPlotByTable(pds: Partial<ScatterData>[], table: KTM[]) {
    if (!this.box.aggregated && !this.graphState.stacked && !this.box.metrics.some(m => m.type === 'area')) return pds;
    // first row wins on duplicate keys
    const rowIndex = new Map<string, number>();
    table.forEach((ktm, i) => {
      const key = GraphPlotUtil.rowKey(ktm.name, ktm.parent?.label);
      if (!rowIndex.has(key)) rowIndex.set(key, i);
    });
    const indexOf = (pd: Partial<ScatterData>) => rowIndex.get(GraphPlotUtil.rowKey(pd.name, pd.meta.parent)) ?? -1;
    return pds.sort((a, b) => indexOf(b) - indexOf(a));
  }

  private static rowKey(name: string, parent: string) {
    return `${name}\0${parent ?? ''}`;
  }

  /** Traces on an overlaying axis draw in a layer above the primary ones; only zorder crosses that. */
  private static readonly AREA_ZORDER = 0;
  private static readonly LINE_ZORDER = 1;

  static isAreaTrace(pd: Partial<ScatterData>): boolean {
    return !!(pd.stackgroup || pd.fill);
  }

  /**
   * Moves line traces after area ones, Plotly draws later traces on top.
   * @param pds plot data array to reorder in place, order inside each group is kept
   */
  static liftLinesAboveAreas(pds: Partial<ScatterData>[]): void {
    const areas = pds.filter(pd => GraphPlotUtil.isAreaTrace(pd));
    if (!areas.length || areas.length === pds.length) return;
    const lines = pds.filter(pd => !GraphPlotUtil.isAreaTrace(pd));
    pds.splice(0, pds.length, ...areas, ...lines);
  }

  /**
   * Decides if item 'Others' can appear
   * @param box graph base data
   * @returns true if can limit loaded items
   */
  static isOthersAllowed(box: GraphBase) {
    return !GraphPlotUtil.isMetricItems(box.metrics, box.aggregated);
  }

  /**
    * Adds threshold lines to series and metrics for plotting
    * @param series timeseries to add thresholds to
    * @param box graph base data with thresholds and metrics
   */
  static withThresholds(series: TimeseriesDTO, box: GraphBase): { series: TimeseriesDTO; metrics: Metric[]; } {
    const thresholds = box.thresholds;
    const baseMetric = box.metrics[0];
    const reference = baseMetric && series.metrics[baseMetric.metric]?.[0];
    if (!thresholds?.length || !reference) {
      return { series, metrics: box.metrics };
    }

    const length = Array.isArray(reference.timeseries) ? reference.timeseries.length : Object.keys(reference.timeseries).length;

    const extraMetrics: Metric[] = [];
    const extraSeries: Record<string, TimeseriesItem[]> = {};
    thresholds.forEach((t, i) => {
      const label = t.label ?? `Threshold (${t.value})`;
      const key = extraSeries[label] ? `${label} (${i})` : label; // de-dupe same-label thresholds
      extraMetrics.push({
        ...baseMetric,
        metric: key,
        label,
        color: t.color ?? '#e32636',
        type: 'line',
        stacked: false,
        order: (baseMetric.order ?? 0) + 100 + i,
        aggregate: undefined,
      });
      extraSeries[key] = [{
        metadata: {
          ...reference.metadata,
          uuid: key,
          name: label,
          avg: t.value,
          max: t.value,
          min: t.value,
          url: null,
          parent: null,
        },
        timeseries: new Array(length).fill(t.value),
      }];
    });

    return {
      series: { ...series, metrics: { ...series.metrics, ...extraSeries } },
      metrics: [...box.metrics, ...extraMetrics],
    };
  }

  /**
   * Formats a duration in seconds as e.g. "45m" or "2h 15m".
   */
  private static formatDuration(seconds: number): string {
    const totalMinutes = Math.round(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  /**
   * Builds traces for trigger periods in the graph
   * @param box graph base data with trigger periods
   * @param height height of the graph in pixels
   */
  static buildTriggerPeriodTraces(box: GraphBase, height: number): Partial<ScatterData>[] {
    if (!box.triggerPeriods?.length || !(height > 0)) return [];

    return box.triggerPeriods.map((period, i): Partial<ScatterData> => {
      const x0 = period.start * 1000 + DateUtil.OFFSET;
      const x1 = period.end * 1000 + DateUtil.OFFSET;
      const from = DateUtil.getDateISO(period.start * 1000, false);
      const to = DateUtil.getDateISO(period.end * 1000, false);
      const duration = GraphPlotUtil.formatDuration(period.end - period.start);
      const text = `Alert triggered\nFrom: ${from}\nTo: ${to}\nDuration: ${duration}`;
      return {
        type: 'scatter',
        mode: 'none',
        name: '',
        x: [x0, x1, x1, x0] as number[],
        y: [0, 0, height, height] as number[],
        fill: 'toself',
        fillcolor: period.color ?? '#e32636',
        opacity: 0.06,
        line: { width: 0 },
        hoveron: 'fills',
        hoverinfo: 'text',
        text,
        showlegend: false,
        meta: { uid: `trigger-period-${i}`, name: 'Triggered', itemName: undefined, metric: 'Triggered', parent: undefined } as PlotDataMeta,
      };
    });
  }

  /**
  * Checks if legend table contains items and metrics
  */
  isMixedMetricItem() {
    return this.box.metrics.some(m => m.legendTop);
  }

  /**
   * @see {@link GraphPlotUtil.isSingleItem}
   */
  isSingleItem() {
    return GraphPlotUtil.isSingleItem(this.box);
  }

  /**
   * Decides if metrics are per table row
   * @param box
   * @returns true to display metrics in rows
   */
  static isSingleItem(box: LayoutProps['box']) {
    return GraphPlotUtil.isMetricItems(box.metrics, box.aggregated) || box.metrics.length > GraphPlotUtil.MAX_GROUP_METRICS;
  }

  /**
   * @see {@link GraphPlotUtil.isMetricItems}
   */
  isMetricItems(metric?: Metric | Metric[]) {
    const metrics = metric ? Array.isArray(metric) ? metric : [metric] : this.box.metrics;
    return GraphPlotUtil.isMetricItems(metrics, this.box.aggregated);
  }

  /**
   * Decides if timeserie items are metrics
   * @param aggregated
   * @param metric optional metric to check aggregation, checks all otherwise
   * @returns true if data items are metrics
   */
  static isMetricItems(metrics: Metric[], aggregated: boolean) {
    return !aggregated || metrics.every(m => m.aggregate && m.aggregate !== METRIC_AGGREGATE.items);
  }

  /**
   * Decides if stacked areas of all metrics share one cumulative stack per direction.
   * @returns true for user-built graphs, predefined ones keep a stack per metric
   */
  private isMergedStack() {
    return this.box.type === 'custom_graph' || this.box.type === 'custom_graph_per_item';
  }

  private isSummable(this: void, pd: Partial<ScatterData>) {
    return pd.visible === true && pd.line.width === 0;
  }

  private isSumMetric(this: void, metric: Metric) {
    return metric.stacked && metric.type === 'area';
  }

  /**
   * Prepares plot and table data
   * @param series timeseries to build data from
   * @param oldData current plot data to maintain visibility
   * @param hiddenItems uids hidden in all graphs of the box; new traces follow it, an empty list leaves
   *   the decision to the other traces (boxes without a shared legend)
   * @returns object with new data
   */
  updatePlotData(series: TimeseriesDTO, oldData: Partial<ScatterData>[], metrics = this.box.metrics, hiddenItems?: readonly string[]): { unitSize: string, plotData: Partial<ScatterData>[], tableData: KTM[]; } {
    this._maxValue = 0;
    this.hiddenItems = hiddenItems?.length ? new Set(hiddenItems) : undefined;
    if (this.graphState.peakMetric) metrics = [...metrics, this.box.peakMetric];

    for (const key in series.metrics) {
      if (Object.hasOwn(series.metrics, key)) {
        const ts = series.metrics[key];
        if (metrics.some(m => m.aggregate === METRIC_AGGREGATE.sum && m.metric === key)) {
          const metricMax = ts.map(ti => ti.metadata.max).reduce((prev, cur) => prev + cur, 0);
          if (metricMax > this.maxValue) this._maxValue = metricMax;
        } else {
          for (const item of ts) {
            if (item.metadata.max && this.maxValue < item.metadata.max) this._maxValue = item.metadata.max;
          }
        }
      }
    }

    this._itemsCountCurrentOthers = series.metadata.others ?? 0;
    this._itemsCountCurrentShown = series.metadata.items ?? 0;

    const metric = metrics[0];
    const sized = metric.defaultPrefix ? {
      unitSizePrefix: metric.defaultPrefix, unitSizedShortcut: metric.defaultPrefix + metric.shortcut,
      magnitude: Math.pow(UnitUtil.getUnitBase(metric.unit), Math.floor(Object.values(UNIT_PREFIX).indexOf(metric.defaultPrefix) / 2) + 1)
    }
      : UnitUtil.sizeUnit(this.maxValue, metric.shortcut, metric.unit);

    let refSumMet: Metric;
    if (!this.box.metrics.some(m => m.label === KTM.ITEM_TOTAL)) {
      refSumMet = this.box.peakMetric || this.box.metrics.find(this.isSumMetric);
      if (!Object.hasOwn(series.metrics, refSumMet?.metric)) {
        refSumMet = null;
        for (const metric of this.box.metrics) {
          if (this.isSumMetric(metric) && Object.hasOwn(series.metrics, metric.metric)) {
            refSumMet = metric;
            break;
          }
        }
      }
    }
    if (refSumMet?.unit === 'percent') {
      refSumMet = null;
    }

    if (!this.common.colorMap) {
      const usedUids = Object.values(series.metrics).flat().sort((a, b) => b.metadata.avg - a.metadata.avg).map(ti => ti.metadata.uuid);
      const mergedUids = [...usedUids, ...this.box.uuids].filter(GLOB.uniqueArrayFilter);
      this.common.colorMap = AbstractGraphPart.mapColors(mergedUids, this.box.metrics, this.box.aggregated).colorPalette;
      if (refSumMet)
        this.common.colorMap[KTM.ITEM_TOTAL + refSumMet.metric] = ColorUtil.TOTAL_COLOR;
    }

    const plotData: Partial<ScatterData>[] = [];
    const tableData: Record<string, KTM> = {};

    for (const key in series.metrics) {
      if (Object.hasOwn(series.metrics, key)) {
        const serie = series.metrics[key];
        const met = metrics.find(m => m.metric === key);
        if (met.aggregate === METRIC_AGGREGATE.avg || met.aggregate === METRIC_AGGREGATE.sum || met.aggregate === METRIC_AGGREGATE.max) {
          plotData.push(this.plotCommon(serie, met, oldData, tableData, sized.magnitude));
        } else {
          for (const item of serie) {
            plotData.push(this.plotCommon(serie, met, oldData, tableData, sized.magnitude, item));
          }
        }
      }
    }

    if (refSumMet && !this.box.metrics.some(m => m.order < 0)) {

      const sumPDs = plotData.filter(this.isSummable);
      if (sumPDs.length > 1) {
        const data = sumPDs.map(pd => pd.y as number[]);
        let timeseries = data.reduce((sum, cur) => sum.map((d, i) => d == null ? cur[i] : d + cur[i]), new Array<number>(data[0].length).fill(null));
        const { avg, max } = GraphPlotUtil.seriesStats(timeseries);
        this.totalSumOffset = max / 1000;
        timeseries = timeseries.map(y => y == null ? null : y + this.totalSumOffset);
        const pd = this.plotCommon([], {
          ...refSumMet,
          metric: this.isMetricItems() ? KTM.ITEM_TOTAL : refSumMet.metric,
          order: 99,
          stacked: false,
          type: 'line',
          label: KTM.ITEM_TOTAL,
          color: ColorUtil.TOTAL_COLOR,
        }, oldData, tableData, sized.magnitude, {
          metadata: {
            ...series.metrics[refSumMet.metric][0].metadata,
            uuid: this.isMetricItems() ? KTM.ITEM_TOTAL : null,
            avg,
            max,
            name: KTM.ITEM_TOTAL,
            url: null,
            parent: null
          },
          timeseries
        });
        plotData.push(pd);
        pd.line.width = 0;
        pd.visible = this.graphState.hoverMode !== 'closest';
        this.totalSumOffset = 0;
      }
    }

    const sortedTable = Object.values(tableData).sort((a, b) => this.compareKTM(a, b));
    const lastTop = sortedTable.findLast(ktm => this.getMetric(ktm)?.legendTop);
    if (lastTop)
      lastTop.props = { ...lastTop.props, separator: true };

    this.sortPlotByTable(plotData, sortedTable);

    // add area gaps grouped by stack after total sum trace
    const gapGroups = new Map<string, Partial<ScatterData>[]>();
    plotData.forEach((pd, i) => {
      if (!pd.stackgroup && !pd.fill) return;
      const key = pd.stackgroup ? `stack:${pd.stackgroup}` : `fill:${i}`;
      const group = gapGroups.get(key);
      if (group) group.push(pd);
      else gapGroups.set(key, [pd]);
    });
    for (const group of gapGroups.values()) GraphPlotUtil.insertAreaGaps(group);

    const numberOfTooltips = Math.round(this.graphState.plotlyRef?.clientHeight / 19);
    const tooltipCut = plotData.length - numberOfTooltips + 3; // 1 for others, 2 for lower padding
    plotData.forEach((pd, index, array) => {
      const meta = pd.meta as PlotDataMeta;
      if (KTM.isTotal(meta)) return;
      if (index < tooltipCut && !KTM.isOthers(meta.name, meta.uid)) {
        pd.hoverinfo = 'skip';
        if (this.graphState.hoverMode !== 'closest') pd.hovertemplate = null;
      } else if (!Array.isArray(pd.hoverinfo)) {
        pd.hoverinfo = null;
      }
    });
    // must run after tooltipCut, which reads trace indexes in legend order
    GraphPlotUtil.liftLinesAboveAreas(plotData);
    return {
      plotData, tableData: sortedTable,
      unitSize: UnitUtil.isBaseUnit(metrics[0].unit) ? metric.shortcut : sized.unitSizedShortcut
    };
  }

  /**
   * Every trace gets an explicit color so the legend square never depends on Plotly's index-based default.
   * A colorMap miss (peak or additional Y-axis metric, colorMap built for a subset) pins the next palette color into the map.
   * @param metric trace metric
   * @param uuid item uuid, ignored for metric-item graphs
   * @returns CSS color
   */
  private traceColor(metric: Metric, uuid: string): string {
    const metricItems = this.isMetricItems(metric);
    if (metricItems && metric.color) return metric.color;
    const colorMap = this.common.colorMap;
    if (!metricItems && colorMap[uuid + GraphPlotUtil.SINGLE_ITEM]) return colorMap[uuid + GraphPlotUtil.SINGLE_ITEM];
    const key = metricItems ? metric.metric : uuid + metric.metric;
    if (colorMap[key]) return colorMap[key];
    const used = new Set(Object.values(colorMap));
    return colorMap[key] = ColorUtil.colorPalette.find(c => !used.has(c))
      ?? ColorUtil.colorPalette[Object.keys(colorMap).length % ColorUtil.colorPalette.length];
  }

  /**
   * Visibility of a trace built from a fresh response.
   * A trace new to the plot follows the box's hidden list when there is one. Otherwise it inherits
   * at most the square-click hiding of the others, `false` being reserved for table-filtered traces
   * (Plotly gives them no meta or color); metrics hidden by configuration (`legendOnly`) do not count as such.
   * @param oldData traces of the previous response, none on first load
   * @param uid trace uid
   * @param otherName metric name
   * @param metric trace metric
   * @returns Plotly visibility
   */
  private inheritVisibility(oldData: Partial<ScatterData>[], uid: string, otherName: string, metric: Metric): ScatterData['visible'] {
    const oldPd = oldData?.find(GraphPlotUtil.traceMatcher(uid, otherName));
    if (oldPd) return oldPd.visible;
    if (metric.legendOnly) return 'legendonly';
    if (this.hiddenItems) return this.hiddenItems.has(uid) ? 'legendonly' : true;
    if (!oldData?.length) return true;
    const legendOnlyLabels = this.legendOnlyLabels();
    const userHidden = oldData.some(pd => GraphPlotUtil.isUserHidden(pd) && !legendOnlyLabels.has((pd.meta as PlotDataMeta)?.metric));
    return userHidden ? 'legendonly' : true;
  }

  /**
   * Fullscreen modal title: menu path, tab and graph name, or the bare graph name where the path adds nothing.
   * @param tabName current tab
   * @param graphName graph title
   * @param plain true for the bare graph name
   * @returns title
   */
  static fullScreenTitle(tabName: string, graphName: string, plain: boolean) {
    if (plain) return graphName;
    const path = MenuNode.getParentNodes(GLOB.selectedItem).map(mn => mn.titleText);
    path.shift();
    path.push(tabName, graphName);
    return path.join(' : ');
  }

  /**
   * Plotly x values from the response metadata, minutes-interval in ms with the local offset applied.
   * @param meta response metadata with start/end seconds and interval minutes
   * @returns timestamps
   */
  static timeAxis(meta: Pick<TimeseriesMetadata, 'start' | 'end' | 'interval'>): number[] {
    const x: number[] = [];
    const datumEnd = meta.end * 1000 + DateUtil.OFFSET;
    const datumIncrement = meta.interval * 1000 * 60;
    for (let datum = meta.start * 1000 + DateUtil.OFFSET; datum <= datumEnd; datum += datumIncrement) {
      x.push(datum);
    }
    return x;
  }

  /**
   * Highest plotted value, null-safe and never below zero.
   * @param plotData traces
   * @returns max y
   */
  static maxY(plotData: Partial<ScatterData>[]): number {
    let max = 0;
    for (const pd of plotData) {
      for (const y of pd.y as number[]) {
        if (max < y) max = y;
      }
    }
    return max;
  }

  /**
   * Assigns each key a palette color not yet used by another key; keys already colored are kept.
   * Falls back to plain rotation once the palette is exhausted.
   * @param keys color map keys
   * @param colorMap map to extend
   */
  static assignUniqueColors(keys: string[], colorMap: ColorMap) {
    const used = new Set(Object.values(colorMap.colorPalette));
    for (const key of keys) {
      if (colorMap.colorPalette[key]) continue;
      let color: string;
      do {
        color = ColorUtil.colorPalette[colorMap.colorIndex++ % ColorUtil.colorPalette.length];
      } while (colorMap.colorIndex < ColorUtil.colorPalette.length && used.has(color));
      colorMap.colorPalette[key] = color;
      used.add(color);
    }
  }

  private plotCommon(serie: TimeseriesItem[], metric: Metric, oldData: Partial<ScatterData>[], tableData: Record<string, KTM>, magnitude: number, item?: TimeseriesItem) {
    const pd: Partial<ScatterData> = {};
    const y: number[] = [];

    if (item) {
      for (const key in item.timeseries) {
        const val: number = item.timeseries[key];
        y.push(val ? (metric.order < 0 ? -val : val) : val);
      }
    } else {
      if (!serie || serie.length < 1) {
        console.debug('No items in timeserie', serie);
        return pd;
      }
      for (const key in serie[0].timeseries) {
        let sum: number = null;
        let max: number = null;
        for (const ti of serie) {
          const val = ti.timeseries[key];
          if (val === null)
            continue;
          sum += Number(val);
          max = Math.max(max, val);
        }
        const aggregateValue = metric.aggregate === METRIC_AGGREGATE.avg ? (sum === null ? null : sum / serie.length)
          : metric.aggregate === METRIC_AGGREGATE.max ? max
            : sum;
        y.push(aggregateValue == null ? aggregateValue : metric.order < 0 ? -aggregateValue : aggregateValue);
      }
    }

    const meta = item ? item.metadata : serie[0].metadata;
    const uuid = meta.uuid || (KTM.isDeleted(meta.name, meta.uuid) ? KTM.ITEM_DELETED : KTM.isOthers(meta.name, meta.uuid) ? KTM.ITEM_OTHERS : meta.uuid);
    pd.x = GraphPlotUtil.timeAxis(meta);
    pd.y = y;
    // scattergl fills not implemented, browsers limit number of figures on page
    pd.type = 'scatter';
    pd.marker = { size: 3 };

    pd.line = { color: this.traceColor(metric, uuid) };
    let uid: string;
    let name: string;
    let otherName: string;
    if (item && !this.isMetricItems(metric)) {
      uid = uuid;
      if (this.box.metrics.length > GraphPlotUtil.MAX_GROUP_METRICS) {
        uid += metric.metric;
        otherName = GraphPlotUtil.SINGLE_ITEM;
      } else {
        otherName = metric.metric;
        pd.legendgrouptitle = { text: otherName };
      }
      name = item.metadata.name;
    } else {
      uid = name = metric.metric;
      otherName = GraphPlotUtil.SINGLE_ITEM;
      pd.legendgrouptitle = { text: otherName };
    }
    pd.name = name;

    const extraNames = [meta?.parent?.label, ...(this.box.properties?.filter(p => p.showInGraph).map(p => p.values[uid]?.value) || [])].filter(Boolean);
    const nameExtra = extraNames?.length ? ` (${extraNames.join(', ')})` : '';
    const itemName = ((item?.metadata?.name ?? '') + nameExtra) || undefined;
    const metricTrace = this.isMetricItems(this.box.metrics.filter(m => m.legendTop === metric.legendTop)) || !itemName;
    const pdm: PlotDataMeta = {
      uid,
      name: metricTrace ? metric.label : itemName,
      itemName,
      metric: metric.label,
      longLabel: metric.longLabel || metric.label,
      metricTrace,
      parent: meta.parent?.label,
    };
    pd.meta = pdm;

    pd.visible = this.inheritVisibility(oldData, uid, otherName, metric);

    if (metric.stacked && this.graphState.stacked) {
      // Mirrored (order<0) areas stack downwards, never summed into the positive stack.
      const dirGroup = metric.order < 0 ? 'neg' : 'one';
      pd.stackgroup = this.isMetricItems() || this.isMergedStack() ? dirGroup : metric.metric;
      pd.line.width = 0;
    } else if (metric.type === 'area' && this.graphState.stacked) {
      pd.line.width = 1;
      pd.fill = 'tozeroy';
    } else {
      pd.line.width = 1;
      GraphPlotUtil.markIsolatedPoints(pd);
    }
    pd.zorder = GraphPlotUtil.isAreaTrace(pd) ? GraphPlotUtil.AREA_ZORDER : GraphPlotUtil.LINE_ZORDER;

    const isBi = this.isBiMetric(metric);
    pd.customdata = pd.y.map(y => y == null ? null : UnitUtil.formatSI(Math.abs(y as number) - this.totalSumOffset, UnitUtil.getUnitFractionDigits(metric.unit), isBi, metric));
    this.assignHovertemplate(pd);

    let ktm = tableData[uid];
    if (!ktm) {
      ktm = new KTM();
      tableData[uid] = ktm;
      if (item)
        ktm.key = metric.metric + item.metadata.name + item.metadata.uuid;
      else
        ktm.key = metric.metric;
      ktm.name = name;
      ktm.parent = meta.parent;
      ktm.metric = metric.label;
      ktm.uid = uid;
      ktm.props = {
        itemProps: this.box.properties?.reduce((prev, current) => ({ ...prev, [current.label]: current.values[uid] }), null as Record<string, GraphTablePropertyItem>)
      };

      if (ktm.isOthers()) {
        this.graphState.othersCount = this._itemsCountCurrentOthers;
        const itemsTotal = this._itemsCountCurrentOthers + this._itemsCountCurrentShown;
        if (this.box.uuids.length > itemsTotal) {
          this.graphState.othersTooltip = (this.box.uuids.length - itemsTotal) + ' items removed (no data)';
        } else {
          this.graphState.othersTooltip = null;
        }
      }
    }

    // compute metadata
    const rendered: Record<string, string | number> = {};
    if (item) {
      ktm.data[otherName] = item.metadata;

      for (const key in item.metadata) {
        if (Object.hasOwn(item.metadata, key)) {
          const val = item.metadata[key];
          rendered[key] = metric === this.box.peakMetric && key === 'avg' ? null : UnitUtil.roundValue(val, magnitude, metric?.unit, metric);
        }
      }
    }
    else {
      // Items peak at different times, so stats come from the aggregated series, not from per-item metadata.
      const metadata: TimeseriesMetadata = { name: name, min: null, ...GraphPlotUtil.seriesStats(y) };
      ktm.data[otherName] = metadata;

      for (const key in metadata) {
        if (Object.hasOwn(metadata, key)) {
          const val = metadata[key];
          rendered[key] = UnitUtil.roundValue(val, magnitude, metric?.unit, metric);
        }
      }
    }

    ktm.rendered[otherName] = rendered;

    return pd;
  }

  /**
   * Legend stats of a plotted series. Magnitudes are used, mirrored (order<0) traces plot below zero.
   * @param y trace values, nulls are gaps
   * @returns null avg/max when the series has no values
   */
  private static seriesStats(y: number[]): { avg: number; max: number; } {
    let sum = 0;
    let max: number = null;
    let count = 0;
    for (const val of y) {
      if (val == null) continue;
      const abs = Math.abs(val);
      sum += abs;
      max = Math.max(max ?? 0, abs);
      count++;
    }
    return { avg: count ? sum / count : null, max };
  }

  /**
   * Shows markers only at values with null neighbours, a lines-only trace draws nothing for them.
   * @param pd line trace to mark
   */
  private static markIsolatedPoints(pd: Partial<ScatterData>) {
    const y = pd.y as number[];
    const opacity = y.map((v, i) => v != null && y[i - 1] == null && y[i + 1] == null ? 1 : 0);
    if (!opacity.includes(1)) return;
    pd.mode = 'lines+markers';
    pd.marker = { ...pd.marker, opacity };
  }

  /**
   * Vertical cut-off on null value.
   * Reindexing x-values for whole stackgroup when inserting 1ms 0 points.
   * Mutates each trace's x/y/customdata.
   * @param traces traces sharing a fill/stack group
   */
  private static insertAreaGaps(traces: Partial<ScatterData>[]) {
    const baseSet = new Set<number>();
    for (const t of traces) {
      if (!Array.isArray(t.x) || !Array.isArray(t.y)) return;
      for (const xv of t.x as number[]) baseSet.add(xv);
    }
    const baseX = [...baseSet].sort((a, b) => a - b);
    if (!baseX.length) return;

    const maps = traces.map(t => {
      const m = new Map<number, { y: number | null; cd: string | null; }>();
      const x = t.x as number[];
      const y = t.y as (number | null)[];
      const cd = Array.isArray(t.customdata) ? t.customdata as (string | null)[] : null;
      x.forEach((xv, i) => m.set(xv, { y: y[i], cd: cd ? cd[i] : null }));
      return m;
    });
    const valAt = (ti: number, j: number) => {
      if (j < 0 || j >= baseX.length) return null;
      return maps[ti].get(baseX[j])?.y ?? null;
    };
    const cdAt = (ti: number, j: number) => {
      if (j < 0 || j >= baseX.length) return null;
      return maps[ti].get(baseX[j])?.cd ?? null;
    };

    // find null boundaries
    const inserts = new Map<number, { j: number; side: 1 | -1; keepValue?: boolean; }>();
    let anyGap = false;
    traces.forEach((_, ti) => {
      for (let j = 0; j < baseX.length; j++) {
        if (valAt(ti, j) == null) { anyGap = true; continue; }
        if (j < baseX.length - 1 && valAt(ti, j + 1) == null) { // null on right
          if (valAt(ti, j - 1) == null) { // null on left, single point spike
            const mid = (baseX[j] + baseX[j + 1]) / 2;
            inserts.set(mid, { j, side: 1 });
            inserts.set(mid - 1, { j, side: 1, keepValue: true });
          } else { // value on left
            inserts.set(baseX[j] + 1, { j, side: 1 });
          }
        }
        if (j > 0 && valAt(ti, j - 1) == null) { // null on left
          if (valAt(ti, j + 1) == null) { // null on right, single point spike
            const mid = (baseX[j] + baseX[j - 1]) / 2;
            inserts.set(mid, { j, side: -1 });
            inserts.set(mid + 1, { j, side: -1, keepValue: true });
          } else { // value on right
            inserts.set(baseX[j] - 1, { j, side: -1 });
          }
        }
      }
    });
    if (!anyGap) return;

    const augX = [...baseX, ...inserts.keys()].sort((a, b) => a - b);
    traces.forEach((t, ti) => {
      const hasCd = Array.isArray(t.customdata);
      const ny: (number | null)[] = [];
      const ncd: (string | null)[] = [];
      for (const ax of augX) {
        const orig = maps[ti].get(ax);
        if (orig) {
          ny.push(orig.y);
          if (hasCd) ncd.push(orig.cd);
          continue;
        }
        const ins = inserts.get(ax);
        // baseX is the UNION of every trace's x, so ax can be from another trace's grid and this
        // one has no sample there. A gap, which is how valAt already scored it in the pass above.
        if (!ins) {
          ny.push(null);
          if (hasCd) ncd.push(null);
          continue;
        }
        const cur = valAt(ti, ins.j);
        // we are at border value
        const isOwnBoundary = cur != null && valAt(ti, ins.j + ins.side) == null;
        // one value spike
        const isIsolated = isOwnBoundary && valAt(ti, ins.j - ins.side) == null;
        // mid-points keep spike value
        ny.push(isOwnBoundary && !ins.keepValue ? 0 : cur);
        // null tooltip for mid-points
        if (hasCd) ncd.push(isIsolated ? null : cdAt(ti, ins.j));
      }
      t.x = augX.slice();
      t.y = ny;
      if (hasCd) t.customdata = ncd;
    });
  }

  /**
   * Decides if metric has base 2 or 10 unit conversion
   * @param metric optional metric with unit to check, uses first graph metric otherwise
   * @returns true if based 2
   */
  static isBiMetric(metric: Metric, maxValue: number) {
    return UnitUtil.isBiUnit(metric.unit) && maxValue > GraphPlotUtil.MIN_SI_VALUE;
  }

  /**
   * @see {@link isBiMetric}
   */
  isBiMetric(metric?: Metric) {
    return GraphPlotUtil.isBiMetric(metric || this.box.metrics[0], this.maxValue);
  }

  /**
   * Set hover template to passed plot data based on hover mode and aggregation
   * @param pd
   * @param hoverMode
   */
  static assignHoverTemplate(pd: Partial<ScatterData>, hoverMode: Layout['hovermode']) {
    const meta: PlotDataMeta = pd.meta;
    if (hoverMode === 'closest') {
      if (meta.itemName) {
        pd.hovertemplate = `<b style='color: ${String(pd.line?.color)}'> ${meta.itemName}</b> <br><br> ${meta.metric}: <b>%{customdata}</b> <br> ${HOVER_X_STYLED} <extra></extra>`;
      } else {
        pd.hovertemplate = `<b style='color: ${String(pd.line?.color)}'> ${meta.metric}:</b> <b>%{customdata}</b> <br> ${HOVER_X_STYLED} <extra></extra>`;
      }
    } else {
      if (pd.hoverinfo === 'skip')
        pd.hovertemplate = null;
      else
        pd.hovertemplate = `<b style='color: ${String(pd.line?.color)}'> ${meta.name}:</b> <b>%{customdata}</b> <extra></extra>`;// <extra>${HOVER_X_FORMAT}</extra>`;
    }
  }

  /**
   * @see {@link assignHoverTemplate}
   * @param pd
   */
  assignHovertemplate(pd: Partial<ScatterData>) {
    GraphPlotUtil.assignHoverTemplate(pd, this.graphState.hoverMode);
  }

  /**
   * Computes the visible Y1 extremes from plot data with stackgroups.
   * @param plotData traces to evaluate
   * @returns lowest and highest stacked/line value across visible primary-axis traces, both including 0
   */
  static plotExtremes(plotData: Partial<ScatterData>[]): YExtremes {
    // stacks with gaps can have different length
    const stacks = new Map<string, Map<number, number>>();
    let max = 0;
    let min = 0;
    const consider = (v: number) => {
      if (v > max) max = v;
      else if (v < min) min = v;
    };
    for (const pd of plotData || []) {
      if (pd.visible !== true || pd.yaxis === 'y2' || !Array.isArray(pd.y)) continue;
      const y = pd.y as number[];
      if (pd.stackgroup) {
        let acc = stacks.get(pd.stackgroup);
        if (!acc) stacks.set(pd.stackgroup, acc = new Map());
        const x = pd.x as number[];
        for (let i = 0; i < y.length; i++) {
          if (y[i]) acc.set(x[i], (acc.get(x[i]) || 0) + y[i]);
        }
      } else {
        y.forEach(consider);
      }
    }
    for (const acc of stacks.values()) {
      acc.forEach(consider);
    }
    return { min, max };
  }

  /**
   * @see {@link plotExtremes}
   * @returns highest visible Y1 value
   */
  private static plotPeak(plotData: Partial<ScatterData>[]) {
    return GraphPlotUtil.plotExtremes(plotData).max;
  }

  /**
   * Set axis tick format or compute for bi metrics
   * @param tblData get data for sum
   * @param plotData to filter visible plots
   * @param props graph properties
   * @param layout to assign ticks
   * @param maxValue minimal Y value to consider
   * @param sharedMax top of a Y range shared with other graphs, may exceed this graph's own data
   */
  static assignTickFormat(tblData: KTM[], plotData: Partial<ScatterData>[], props: LayoutProps, layout: Partial<Layout>, maxValue: number, sharedMax?: number) {
    if (sharedMax > maxValue) maxValue = sharedMax;
    if (GraphPlotUtil.isBiMetric(props.box.metrics[0], maxValue)) {
      let maxSum = GraphPlotUtil.plotPeak(plotData);
      if (!maxSum) {
        // no plot data, use table maxes
        if (GraphPlotUtil.isSingleItem(props.box)) {
          maxSum = sumax(tblData, GraphPlotUtil.SINGLE_ITEM, props.box.metrics.some(m => m.stacked));
        } else {
          for (const metric of props.box.metrics) {
            maxSum = Math.max(maxSum, sumax(tblData, metric.metric, metric.stacked));
          }
        }
        if (maxValue) maxSum = Math.max(maxValue, maxSum);
      }
      maxSum = Math.max(maxSum, props.box.max || 0, sharedMax || 0);
      let reducedBi = maxSum;
      let power = -1;
      while (reducedBi >= 1024) {
        power++;
        reducedBi = reducedBi / 1024;
      }

      const sig2tmp = Number(String(reducedBi * 10).substring(0, 2));
      const significant2 = props.type === GraphType.modal ? Math.max(10, sig2tmp / 2) : sig2tmp;
      const vals: number[] = [];
      const texts: string[] = [];
      let step: number;
      if (significant2 <= 12) {
        step = 0.2;
      } else if (significant2 <= 28) {
        step = 0.5;
      } else if (significant2 <= 56) {
        step = 1;
      } else {// if (significant2 >= 57)
        step = 2;
      }
      const magnitudeStep = Math.pow(10, Math.floor(Math.log10(reducedBi)));
      const dtick = step * magnitudeStep;
      const magnitude = Math.pow(1024, power + 1);
      function sig(num: number) { return dtick < 1 ? num === Math.round(num) ? num : Number(num.toFixed(1)) : num; }
      let tick = sig(Math.round(reducedBi / dtick) * dtick);
      vals.push(tick * magnitude);
      if (String(tick).length > 3) {
        texts.push(tick / 1000 + UnitUtil.getSiSymbol(power + 1, true));
      } else
        texts.push(tick + UnitUtil.getSiSymbol(power, true));
      while ((tick = sig(tick - dtick)) > 0) {
        vals.push(tick * magnitude);
        texts.push(tick + UnitUtil.getSiSymbol(power, true));
      }
      vals.push(0);
      texts.push('0');
      layout.yaxis.tickvals = vals;
      layout.yaxis.ticktext = texts;
    }
    else
      layout.yaxis.tickformat = maxValue < GraphPlotUtil.MIN_SI_VALUE || !GraphPlotUtil.isSiPrefix(props.box.metrics[0].unit, props.type) ? TICK_FORMAT_FIXED : TICK_FORMAT_SI;

    layout.xaxis.showticklabels = !!tblData?.length;
  }

  /**
   * Should SI prefix be used
   * @param unit
   * @param graphType
   * @returns true for dashboards and non base units
   */
  static isSiPrefix(unit: Unit, graphType: GraphType) {
    return graphType === GraphType.dashboard || !UnitUtil.isBaseUnit(unit);
  }

}
